import React, { useEffect, useState } from 'react';
import { DashboardLayout } from '../../layouts/DashboardLayout';
import { useAuth } from '../../contexts/AuthContext';
import {
  getCategories, createCategory, updateCategory, deleteCategory,
  resetAndSeedCategories, autoCategorizeProducts, uploadCategoryImage,
  getMasterCategories
} from '../../services/api';
import { Tags, Plus, Edit, Trash2, X, Sparkles, RefreshCw, Wand2, Upload, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import { getErrorMessage } from '../../utils/errorUtils';

const Categorias = () => {
  const { user } = useAuth();
  const [categories, setCategories] = useState([]);
  const [masterCategories, setMasterCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [filterMasterCatId, setFilterMasterCatId] = useState('');
  const [actionLoading, setActionLoading] = useState('');
  const [uploadingImageFor, setUploadingImageFor] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    icon: '',
    slug: '',
    order: 0,
    is_active: true,
    master_category_id: '',
    master_category_name: '',
    product_examples: '',
    image_url: '',
    thumbnail_url: '',
  });

  useEffect(() => {
    loadCategories();
    loadMasterCategories();
  }, []);

  const loadMasterCategories = async () => {
    try {
      const response = await getMasterCategories();
      setMasterCategories(response.data);
    } catch (error) {
      console.error('Error loading master categories:', error);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await getCategories(false);
      setCategories(response.data);
    } catch (error) {
      console.error('Error loading categories:', error);
      toast.error('Error al cargar categorías');
    } finally {
      setLoading(false);
    }
  };

  const handleResetAndSeed = async () => {
    if (!window.confirm('Esto ELIMINARÁ todas las categorías actuales y creará 75 nuevas. ¿Continuar?')) return;

    setActionLoading('reset');
    try {
      const response = await resetAndSeedCategories();
      toast.success(response.data.message);
      loadCategories();
    } catch (error) {
      console.error('Error resetting categories:', error);
      toast.error(getErrorMessage(error, 'Error al reiniciar categorías'));
    } finally {
      setActionLoading('');
    }
  };

  const handleAutoCategorize = async () => {
    if (!window.confirm('Esto asignará categorías automáticamente a productos existentes por coincidencia de nombre. ¿Continuar?')) return;

    setActionLoading('autocategorize');
    try {
      const response = await autoCategorizeProducts();
      toast.success(`${response.data.categorized} productos categorizados, ${response.data.skipped} omitidos`);
    } catch (error) {
      console.error('Error auto-categorizing:', error);
      toast.error(getErrorMessage(error, 'Error al auto-categorizar'));
    } finally {
      setActionLoading('');
    }
  };

  const handleImageUpload = async (categoryId, file) => {
    if (!file) return;
    setUploadingImageFor(categoryId);
    try {
      const response = await uploadCategoryImage(categoryId, file);
      toast.success('Imagen subida exitosamente');
      loadCategories();
    } catch (error) {
      console.error('Error uploading category image:', error);
      toast.error(getErrorMessage(error, 'Error al subir imagen'));
    } finally {
      setUploadingImageFor(null);
    }
  };

  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const data = { ...formData };
      if (!data.slug) {
        data.slug = generateSlug(data.name);
      }

      if (editingCategory) {
        await updateCategory(editingCategory.id, data);
        toast.success('Categoría actualizada');
      } else {
        await createCategory(data);
        toast.success('Categoría creada');
      }
      setShowModal(false);
      resetForm();
      loadCategories();
    } catch (error) {
      console.error('Error saving category:', error);
      toast.error(getErrorMessage(error, 'Error al guardar categoría'));
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      description: category.description || '',
      icon: category.icon || '',
      slug: category.slug || '',
      order: category.order || 0,
      is_active: category.is_active,
      master_category_id: category.master_category_id || '',
      master_category_name: category.master_category_name || '',
      product_examples: category.product_examples || '',
      image_url: category.image_url || '',
      thumbnail_url: category.thumbnail_url || '',
    });
    setShowModal(true);
  };

  const handleDelete = async (category) => {
    if (!window.confirm(`¿Estás seguro de eliminar "${category.name}"?`)) return;

    try {
      await deleteCategory(category.id);
      toast.success('Categoría eliminada');
      loadCategories();
    } catch (error) {
      console.error('Error deleting category:', error);
      toast.error('Error al eliminar categoría');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      icon: '',
      slug: '',
      order: 0,
      is_active: true,
      master_category_id: '',
      master_category_name: '',
      product_examples: '',
      image_url: '',
      thumbnail_url: '',
    });
    setEditingCategory(null);
  };

  const filteredCategories = filterMasterCatId
    ? categories.filter(c => c.master_category_id === filterMasterCatId)
    : categories;

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Categorías de Productos</h1>
          <p className="text-slate-500 mt-1">75 categorías organizadas en 6 categorías maestras</p>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap justify-between items-center gap-3">
          <p className="text-slate-600">{filteredCategories.length} de {categories.length} categorías</p>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={handleResetAndSeed}
              disabled={actionLoading === 'reset'}
              className="inline-flex items-center gap-2 px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium transition-colors text-sm disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${actionLoading === 'reset' ? 'animate-spin' : ''}`} />
              Reiniciar 75 Categorías
            </button>
            <button
              onClick={handleAutoCategorize}
              disabled={actionLoading === 'autocategorize'}
              className="inline-flex items-center gap-2 px-3 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 font-medium transition-colors text-sm disabled:opacity-50"
            >
              <Wand2 className={`w-4 h-4 ${actionLoading === 'autocategorize' ? 'animate-pulse' : ''}`} />
              Auto-categorizar Productos
            </button>
            <button
              onClick={() => {
                resetForm();
                setShowModal(true);
              }}
              className="inline-flex items-center gap-2 px-3 py-2 bg-teal-700 text-white rounded-lg hover:bg-teal-800 font-medium transition-colors text-sm"
              data-testid="add-category-btn"
            >
              <Plus className="w-4 h-4" />
              Nueva Categoría
            </button>
          </div>
        </div>

        {/* Master Category Filter Chips */}
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setFilterMasterCatId('')}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
              !filterMasterCatId
                ? 'bg-teal-700 text-white'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            Todas ({categories.length})
          </button>
          {masterCategories.map((mc) => {
            const count = categories.filter(c => c.master_category_id === mc.id).length;
            return (
              <button
                key={mc.id}
                onClick={() => setFilterMasterCatId(mc.id === filterMasterCatId ? '' : mc.id)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  filterMasterCatId === mc.id
                    ? 'bg-teal-700 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {mc.icon} {mc.name} ({count})
              </button>
            );
          })}
        </div>

        {/* Categories Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredCategories.map((category) => (
            <div
              key={category.id}
              className={`bg-white rounded-xl border ${
                category.is_active ? 'border-slate-200' : 'border-red-200 bg-red-50'
              } p-4 hover:shadow-md transition-shadow`}
            >
              <div className="flex items-start gap-3">
                {/* Image or fallback */}
                <div className="relative flex-shrink-0">
                  {category.image_url || category.thumbnail_url ? (
                    <img
                      src={category.thumbnail_url || category.image_url}
                      alt={category.name}
                      className="w-14 h-14 rounded-lg object-cover border border-slate-200"
                    />
                  ) : (
                    <div className="w-14 h-14 rounded-lg bg-slate-100 flex items-center justify-center border border-slate-200">
                      <ImageIcon className="w-6 h-6 text-slate-300" />
                    </div>
                  )}
                  {/* Upload image button */}
                  <label className="absolute -bottom-1 -right-1 p-1 bg-white border border-slate-200 rounded-full cursor-pointer hover:bg-slate-50 shadow-sm">
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      disabled={uploadingImageFor === category.id}
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) handleImageUpload(category.id, file);
                        e.target.value = '';
                      }}
                    />
                    {uploadingImageFor === category.id ? (
                      <div className="w-3.5 h-3.5 animate-spin rounded-full border-b-2 border-teal-600"></div>
                    ) : (
                      <Upload className="w-3.5 h-3.5 text-slate-400" />
                    )}
                  </label>
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between">
                    <div className="min-w-0">
                      <h3 className="font-semibold text-slate-900 text-sm truncate">{category.name}</h3>
                      {category.master_category_name && (
                        <span className="inline-block px-1.5 py-0.5 rounded text-xs bg-blue-50 text-blue-600 font-medium mt-0.5">
                          {category.master_category_name}
                        </span>
                      )}
                    </div>
                    <div className="flex gap-0.5 flex-shrink-0 ml-1">
                      <button
                        onClick={() => handleEdit(category)}
                        className="p-1 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                      >
                        <Edit className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => handleDelete(category)}
                        className="p-1 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                  {category.product_examples && (
                    <p className="text-xs text-slate-400 mt-1 line-clamp-1">{category.product_examples}</p>
                  )}
                </div>
              </div>
              <div className="flex items-center justify-between mt-3 pt-2 border-t border-slate-100">
                <span className="text-xs text-slate-400">#{category.order}</span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                  category.is_active
                    ? 'bg-green-100 text-green-700'
                    : 'bg-red-100 text-red-700'
                }`}>
                  {category.is_active ? 'Activa' : 'Inactiva'}
                </span>
              </div>
            </div>
          ))}
        </div>

        {filteredCategories.length === 0 && (
          <div className="bg-white rounded-xl p-12 text-center border border-slate-200">
            <Tags className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-700">No hay categorías</h3>
            <p className="text-slate-500 mt-1">Crea las 75 categorías predefinidas para empezar</p>
            <button
              onClick={handleResetAndSeed}
              disabled={actionLoading === 'reset'}
              className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium transition-colors"
            >
              <Sparkles className="w-4 h-4" />
              Crear 75 Categorías
            </button>
          </div>
        )}

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <div className="bg-white rounded-xl shadow-xl max-w-lg w-full my-8">
              <div className="flex items-center justify-between p-6 border-b border-slate-200">
                <h2 className="text-2xl font-bold text-slate-900">
                  {editingCategory ? 'Editar Categoría' : 'Nueva Categoría'}
                </h2>
                <button
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-slate-500" />
                </button>
              </div>
              <form onSubmit={handleSubmit} className="p-6 space-y-4 max-h-[calc(100vh-200px)] overflow-y-auto">
                {/* Master Category */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Categoría Maestra *</label>
                  <select
                    required
                    value={formData.master_category_id}
                    onChange={(e) => {
                      const selected = masterCategories.find(c => c.id === e.target.value);
                      setFormData({
                        ...formData,
                        master_category_id: e.target.value,
                        master_category_name: selected ? selected.name : ''
                      });
                    }}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500"
                  >
                    <option value="">Seleccionar categoría maestra...</option>
                    {masterCategories.map((mc) => (
                      <option key={mc.id} value={mc.id}>
                        {mc.icon} {mc.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Nombre *</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => {
                      const name = e.target.value;
                      setFormData({
                        ...formData,
                        name,
                        slug: editingCategory ? formData.slug : generateSlug(name)
                      });
                    }}
                    placeholder="Ej: Abarrotes"
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Slug</label>
                    <input
                      type="text"
                      value={formData.slug}
                      onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                      placeholder="auto-generado"
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 text-sm font-mono"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Orden</label>
                    <input
                      type="number"
                      min="0"
                      value={formData.order}
                      onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Descripción</label>
                  <input
                    type="text"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Breve descripción de la categoría"
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Ejemplos de Productos</label>
                  <textarea
                    rows="2"
                    value={formData.product_examples}
                    onChange={(e) => setFormData({ ...formData, product_examples: e.target.value })}
                    placeholder="arroz, frijol, aceite, azúcar (separados por coma)"
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 text-sm"
                  />
                  <p className="text-xs text-slate-400 mt-1">Se usan para auto-categorizar productos</p>
                </div>

                {/* Image preview for editing */}
                {editingCategory && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Imagen de Categoría</label>
                    <div className="flex items-center gap-3">
                      {formData.image_url ? (
                        <img src={formData.thumbnail_url || formData.image_url} alt="" className="w-16 h-16 rounded-lg object-cover border" />
                      ) : (
                        <div className="w-16 h-16 rounded-lg bg-slate-100 flex items-center justify-center border">
                          <ImageIcon className="w-6 h-6 text-slate-300" />
                        </div>
                      )}
                      <label className="cursor-pointer inline-flex items-center gap-2 px-3 py-1.5 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 text-sm font-medium">
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) {
                              handleImageUpload(editingCategory.id, file);
                            }
                            e.target.value = '';
                          }}
                        />
                        <Upload className="w-4 h-4" />
                        Subir Imagen
                      </label>
                      <span className="text-xs text-slate-400">Max 5MB, 350x350px</span>
                    </div>
                  </div>
                )}

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="cat_is_active"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                  />
                  <label htmlFor="cat_is_active" className="text-sm text-slate-700">Categoría activa</label>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-teal-700 text-white rounded-lg hover:bg-teal-800 font-medium transition-colors"
                  >
                    {editingCategory ? 'Actualizar' : 'Crear'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                    }}
                    className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 font-medium transition-colors"
                  >
                    Cancelar
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Categorias;
